import { useId } from "react";
import type { Icon } from "@phosphor-icons/react";

import { cn } from "@/lib/utils";

interface OnboardingChoiceCardProps {
  label: string;
  /** Sentence under the label — every card in these frames has one. */
  description: string;
  icon: Icon;
  selected: boolean;
  /** Shared across the group, so arrow keys move between the cards. */
  name: string;
  onSelect: () => void;
}

/**
 * Full-width selectable card used by the two single-choice steps
 * (`15984:128874` / `15984:128875`): a 56pt icon tile, then the label and its
 * description.
 *
 * Built on a real `<input type="radio">` rather than `role="radio"` on a div —
 * that is what gives arrow-key navigation between the cards, a single tab stop
 * for the group and the right announcement, all without hand-rolled key
 * handling. The input is `sr-only` rather than hidden, so it keeps its place in
 * the tab order, and the focus ring is painted on the whole card through
 * `has-[:focus-visible]` because a ring around an invisible input would be a
 * ring around nothing.
 */
const OnboardingChoiceCard = ({
  label,
  description,
  icon: IconComponent,
  selected,
  name,
  onSelect,
}: OnboardingChoiceCardProps) => {
  const descriptionId = useId();

  return (
    <label
      className={cn(
        "flex cursor-pointer flex-col gap-2 rounded-lg p-3 transition-colors",
        "has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-focusRing-default",
        // 1.5px rather than 2 when selected, so the content doesn't shift by
        // half a pixel as the border thickens.
        selected
          ? "border-[1.5px] border-border-token-brand-subtle bg-bg-brand-tertiary-default"
          : "border border-border-token-brand-minimal bg-bg-brand-secondary-default hover:bg-bg-brand-secondary-hover",
      )}
    >
      <input
        type="radio"
        name={name}
        checked={selected}
        onChange={onSelect}
        aria-describedby={descriptionId}
        className="sr-only"
      />

      {/* The tile flips to white on a selected card, which has gone
          brand-tinted underneath it — otherwise it disappears into the card. */}
      <span
        className={cn(
          "flex h-14 w-14 items-center justify-center rounded",
          selected
            ? "bg-container-neutral-white"
            : "bg-container-brand-minimal",
        )}
      >
        <IconComponent
          size={32}
          aria-hidden="true"
          className="text-fg-brand-subtle"
        />
      </span>

      <span className="flex flex-col">
        <span className="body-l-semibold text-fg-neutral-strong">{label}</span>
        <span
          id={descriptionId}
          className="label-s-regular text-fg-neutral-subtle"
        >
          {description}
        </span>
      </span>
    </label>
  );
};

export default OnboardingChoiceCard;
